import { generateColor } from './generate-color';
import { parseHslStringToHslArgs } from './parse-hsl-string-to-hsl-args';

const hexToRgb = (hex) => {
  if (typeof hex !== 'string') return null;

  let normalized = hex.trim();
  if (!normalized.startsWith('#')) return null;

  normalized = normalized.slice(1);

  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((c) => `${c}${c}`)
      .join('');
  }

  if (normalized.length !== 6) return null;

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  if ([r, g, b].some((v) => Number.isNaN(v))) return null;

  return { r, g, b };
};

const getServiceColorKey = (appointmentOrEvent) => {
  const sellerServiceUid =
    appointmentOrEvent?.sellerService?.uid ||
    appointmentOrEvent?.sellerServiceUid;

  const sellerServiceName =
    appointmentOrEvent?.sellerService?.name ||
    appointmentOrEvent?.service ||
    appointmentOrEvent?.sellerServiceName;

  return sellerServiceUid || sellerServiceName || '';
};

const getPersistedServiceColor = (appointmentOrEvent) => {
  return (
    appointmentOrEvent?.sellerService?.color ||
    appointmentOrEvent?.sellerServiceColor ||
    ''
  );
};

export const getAppointmentServiceColor = (appointmentOrEvent) => {
  const persistedColor = getPersistedServiceColor(appointmentOrEvent);
  if (persistedColor) return persistedColor;

  const key = getServiceColorKey(appointmentOrEvent);
  if (!key) return '';

  return generateColor(key);
};

export const getAppointmentServiceStyle = (appointmentOrEvent) => {
  const baseColor = getAppointmentServiceColor(appointmentOrEvent);

  if (!baseColor) {
    return {};
  }

  let backgroundColor = '#f7f7f7';

  if (baseColor.startsWith('hsl')) {
    const hslArgs = parseHslStringToHslArgs(baseColor);
    const h = hslArgs?.[0];
    const s = hslArgs?.[1];

    backgroundColor = Number.isFinite(h)
      ? `hsla(${h}, ${s}%, 92%, 0.22)`
      : '#f7f7f7';
  } else {
    const rgb = hexToRgb(baseColor);
    if (rgb) {
      backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.18)`;
    } else if (baseColor.startsWith('rgb(')) {
      backgroundColor = baseColor
        .replace(/^rgb\(/, 'rgba(')
        .replace(/\)$/, ', 0.18)');
    }
  }

  const style = {
    backgroundColor,
    borderLeft: `4px solid ${baseColor}`,
    color: '#111827',
  };

  if (appointmentOrEvent?.status === 'completed') {
    style.opacity = 0.55;
    style.filter = 'grayscale(0.8) saturate(0.6)';
  }

  if (
    appointmentOrEvent?.status === 'canceled' ||
    appointmentOrEvent?.status === 'rejected'
  ) {
    style.opacity = 0.55;
    style.textDecoration = 'line-through';
    style.filter = 'grayscale(0.5)';
  }

  return style;
};

export const getAppointmentCalendarEventStyle = (event, isSelected) => {
  const baseColor = getAppointmentServiceColor(event);
  const serviceStyle = getAppointmentServiceStyle(event);

  return {
    ...serviceStyle,
    border: `1px solid ${baseColor || '#e5e7eb'}`,
    boxShadow: isSelected ? 'inset 0 0 0 2px #2f80ed' : undefined,
  };
};
